<template>
  <div :class="$style.board">
    <header v-if="!lockedByUnlockConditions" :class="$style.header">
      <div :class="$style.headerInfo">
        <div :class="$style.stageInfo">
          <h2 :class="$style.title">{{ stage.name }}</h2>
          <pickem-countdown
            v-if="timerTo(stage)"
            :to="timerTo(stage)"
            :class="$style.timerChip"
          >
            <ui-icon name="clock-filled" :class="$style.timerIcon" />
          </pickem-countdown>
          <stage-condition-tag v-bind="stageConditionTag" />
        </div>
        <p v-if="subtitle" :class="$style.subtitle">{{ subtitle }}</p>
      </div>
      <div :class="$style.actions">
        <pick-result-toggle
          v-if="canToggle"
          v-model="modeRef"
          :class="$style.toggle"
        />
      </div>
    </header>

    <unlock-banner
      v-if="showUnlockBanner"
      :stage="stage"
      :unlock="stage.unlock"
      is-compact
    />

    <div
      :class="[$style.scroll, locked && !isResultsView && $style.scrollDimmed]"
    >
      <div :class="$style.viewport" :style="viewportStyle">
        <template v-if="layout.mode === 'gsl-dual-track'">
          <section
            v-for="section in layout.sections"
            :key="section.id"
            :class="$style.section"
            :style="sectionStyle(section)"
          >
            <h3 :class="$style.sectionTitle">
              {{ t(section.labelKey) }}
            </h3>

            <div :class="$style.headers">
              <div
                v-for="column in section.columns"
                :key="column.code"
                :class="$style.headerCell"
                :style="sectionHeaderStyle(column, section)"
              >
                <span :class="$style.columnTitle">{{ column.label }}</span>
              </div>
            </div>
          </section>
        </template>

        <template v-else>
          <!-- Шапка с названиями колонок: позиционируется поверх grid'а.
               По figma: Subhead/L (17/24) text-3, без счётчика матчей. -->
          <div :class="$style.headers">
            <div
              v-for="column in layout.columns"
              :key="column.code"
              :class="$style.headerCell"
              :style="headerStyle(column)"
            >
              <span
                :class="[
                  $style.columnTitle,
                  column.code === BRACKET_WINNER_COLUMN_CODE &&
                    $style.columnTitleWinner
                ]"
              >
                {{
                  column.code === BRACKET_WINNER_COLUMN_CODE
                    ? t('winnerColumn')
                    : column.label
                }}
              </span>
            </div>
          </div>
        </template>

        <!-- Связки между матчами рисуем SVG-полилиниями с тремя сегментами:
             горизонталь от source к точке поворота, вертикаль до уровня target,
             горизонталь к target. Цвет — border (#555F71), как в макете. -->
        <svg
          :class="$style.connectors"
          :viewBox="connectorViewBox"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <polyline
            v-for="(connector, index) in layoutConnectors"
            :key="`${connector.from.matchId}-${connector.to.matchId}-${connector.to.x}-${index}`"
            :points="connectorPoints(connector)"
            fill="none"
            stroke="var(--root-bg-border, #555F71)"
            stroke-width="0.2"
          />
        </svg>

        <!-- Карточки матчей абсолютно позиционируются по computed (x, y). -->
        <template v-for="placed in layoutMatches" :key="placed.match.id">
          <bracket-match
            v-if="tournamentStore.tournament"
            :match="placed.match"
            :stage="stage"
            :tournament="tournamentStore.tournament"
            :locked="locked"
            :results-view="isResultsView"
            :in-flight="inFlightMatchIds.has(placed.match.id)"
            :caption="getMatchCaption(placed.match.id)"
            :class="$style.matchCard"
            :style="matchStyle(placed)"
            @pick="onPick"
          />
        </template>

        <bracket-winner
          v-if="layout.winner"
          :team="winnerTeam"
          :is-pick="isWinnerUserPick"
          :is-correct="isWinnerCorrect"
          :class="$style.winnerCard"
          :style="getWinnerCardStyle(layout.winner)"
        />

        <bracket-winner
          v-if="layout.thirdPlaceWinner"
          variant="bronze"
          :team="thirdPlaceWinnerTeam"
          :is-pick="isThirdPlaceWinnerUserPick"
          :is-correct="isThirdPlaceWinnerCorrect"
          :class="$style.winnerCard"
          :style="getWinnerCardStyle(layout.thirdPlaceWinner)"
        />
      </div>
    </div>
  </div>
</template>

<i18n lang="yaml">
ru:
  winnerColumn: Winner
  thirdPlace: Матч за 3-е место
  upperBracket: Upper Bracket
  lowerBracket: Lower Bracket
  awaitingResults: Выбор недоступен, этап уже начался
  finished: Этап завершён
</i18n>

<script lang="ts" setup>
import { computed, ref, watch, type CSSProperties } from 'vue'
import UiIcon from '@/ui-core/components/icon/index.vue'
import BracketMatch from './BracketMatch.vue'
import BracketWinner from './BracketWinner.vue'
import PickResultToggle, {
  type PickResultMode
} from '../swiss/PickResultToggle.vue'
import UnlockBanner from '../unlock/UnlockBanner.vue'
import PickemCountdown from '../../common/Countdown.vue'
import StageConditionTag from '../StageConditionTag.vue'
import {
  getStageConditionTag,
  timerTo
} from '../../../utils/get-stage-condition-tag'

import { usePredictionsStore } from '../../../stores/predictions'
import { useTournamentStore } from '../../../stores/tournament'
import {
  usePickemAnalytics,
  type PickemPickAnalyticsMeta
} from '../../../composables/use-pickem-analytics'
import {
  useBracketLayout,
  BRACKET_LOWER_ROW_UNIT_EM,
  BRACKET_MATCH_WIDTH_EM,
  BRACKET_MATCH_HEIGHT_EM,
  BRACKET_UNIT_EM,
  BRACKET_WINNER_COLUMN_CODE,
  BRACKET_GF_COLUMN_CODE,
  GSL_LOWER_COLUMN_CODES
} from '../../../composables/use-bracket-layout'
import { getBracketGfWinner } from '../../../utils/get-bracket-gf-winner'
import { getBracketSlotDisplayTeam } from '../../../utils/bracket-slot-display'
import { isBracketMatchLockedByDownstreamPicks } from '../../../utils/bracket-match-lock'
import type {
  Stage,
  BracketMatch as BracketMatchT,
  BracketSlot
} from '../../../types'
import type {
  BracketLayoutConnector,
  BracketLayoutMatch,
  BracketLayoutSection,
  BracketLayoutWinner
} from '../../../composables/use-bracket-layout'

defineOptions({ name: 'PickemBracketBoard' })

const props = defineProps<{
  stage: Stage & { bracket: NonNullable<Stage['bracket']> }
  showUnlockBanner?: boolean
}>()

const { $snacks } = useNuxtApp()
const { t } = useI18n()

const predictionsStore = usePredictionsStore()
const tournamentStore = useTournamentStore()
const { trackSwitch, trackPickClick } = usePickemAnalytics()

const cssModule = useCssModule()

const lockedByUnlockConditions = computed(
  () =>
    Boolean(props.stage.unlock?.required) && !props.stage.unlock?.user_unlocked
)

const locked = computed(
  () => props.stage.ui_state !== 'pickable' || lockedByUnlockConditions.value
)

const stageConditionTag = computed(() => getStageConditionTag(props.stage))

const canToggle = computed(() => props.stage.result_view_available)

const modeRef = ref<PickResultMode>(canToggle.value ? 'result' : 'pick')

const skipSwitchTrack = ref(false)

watch(
  () => props.stage.ui_state,
  next => {
    skipSwitchTrack.value = true
    modeRef.value =
      next === 'awaiting_results' || next === 'finished' ? 'result' : 'pick'
  }
)

watch(modeRef, (value, previousValue) => {
  if (skipSwitchTrack.value) {
    skipSwitchTrack.value = false
    return
  }
  if (value === previousValue || !tournamentStore.tournament) return

  trackSwitch({
    tabValue: value,
    stage: props.stage,
    tournament: tournamentStore.tournament
  })
})

const isResultsView = computed(
  () => canToggle.value && modeRef.value === 'result'
)

const { layout } = useBracketLayout(() => props.stage.bracket)

const layoutMatches = computed(() => Array.from(layout.value.matches.values()))

const layoutConnectors = computed(() => layout.value.connectors)

const getMatchById = (matchId: number | undefined) =>
  props.stage.bracket.matches.find(match => match.id === matchId)

const grandFinalMatch = computed(() => {
  const winnerMatchId = layout.value.winner?.matchId
  if (winnerMatchId) return getMatchById(winnerMatchId) ?? null

  return (
    props.stage.bracket.matches.find(
      match => match.column_code === BRACKET_GF_COLUMN_CODE
    ) ?? null
  )
})

const thirdPlaceMatch = computed(() => {
  const matchId = layout.value.thirdPlaceWinner?.matchId
  return matchId ? getMatchById(matchId) ?? null : null
})

const getWinnerCardTeam = (match: BracketMatchT | null) => {
  if (!match) return null
  return getBracketGfWinner(match, isResultsView.value)
}

const getIsWinnerCardUserPick = (
  match: BracketMatchT | null,
  team: ReturnType<typeof getWinnerCardTeam>
) => {
  if (!match || !team || isResultsView.value || locked.value) return false
  return match.user_pick_team_id === team.id && !match.outcome_side
}

const getIsWinnerCardCorrect = (
  match: BracketMatchT | null,
  team: ReturnType<typeof getWinnerCardTeam>
): boolean | null => {
  if (!match || !team || isResultsView.value) return null
  if (match.user_pick_team_id !== team.id) return null
  return match.is_correct
}

const winnerTeam = computed(() => getWinnerCardTeam(grandFinalMatch.value))

const isWinnerUserPick = computed(() =>
  getIsWinnerCardUserPick(grandFinalMatch.value, winnerTeam.value)
)

const isWinnerCorrect = computed(() =>
  getIsWinnerCardCorrect(grandFinalMatch.value, winnerTeam.value)
)

const thirdPlaceWinnerTeam = computed(() =>
  getWinnerCardTeam(thirdPlaceMatch.value)
)

const isThirdPlaceWinnerUserPick = computed(() =>
  getIsWinnerCardUserPick(thirdPlaceMatch.value, thirdPlaceWinnerTeam.value)
)

const isThirdPlaceWinnerCorrect = computed(() =>
  getIsWinnerCardCorrect(thirdPlaceMatch.value, thirdPlaceWinnerTeam.value)
)

const getMatchCaption = (matchId: number) => {
  if (layout.value.thirdPlace?.matchId !== matchId) return null
  return layout.value.thirdPlace.label || t('thirdPlace')
}

const inFlightMatchIds = computed<ReadonlySet<number>>(() => {
  const set = new Set<number>()
  for (const match of props.stage.bracket.matches) {
    if (predictionsStore.isBracketInFlight(props.stage.id, match.id)) {
      set.add(match.id)
    }
  }
  return set
})

const subtitle = computed(() => {
  switch (props.stage.ui_state) {
    case 'awaiting_results':
      return t('awaitingResults')
    case 'finished':
      return t('finished')
    default:
      return null
  }
})

// Линейный пресет: 4em под заголовки колонок + метку над первой парой.
// GSL dual-track: высота уже заложена в layout.height (секции + gap).
const HEADER_HEIGHT_EM = 4

const isGslLayout = computed(() => layout.value.mode === 'gsl-dual-track')

const em = (value: number) => `${value}em`

const viewportStyle = computed<CSSProperties>(() => ({
  width: em(layout.value.width),
  height: em(layout.value.height + (isGslLayout.value ? 0 : HEADER_HEIGHT_EM))
}))

const connectorViewBox = computed(
  () =>
    `0 0 ${layout.value.width} ${
      layout.value.height + (isGslLayout.value ? 0 : HEADER_HEIGHT_EM)
    }`
)

const headerStyle = (column: { x: number; width: number }): CSSProperties => ({
  left: em(column.x),
  width: em(column.width)
})

const sectionStyle = (section: BracketLayoutSection): CSSProperties => ({
  top: em(section.top),
  height: em(section.height)
})

const sectionHeaderStyle = (
  column: { x: number; width: number },
  section: BracketLayoutSection
): CSSProperties => ({
  left: em(column.x),
  width: em(column.width),
  top: em(section.columnHeaderTop - section.top)
})

const upperMatchYOffset = (BRACKET_UNIT_EM - BRACKET_MATCH_HEIGHT_EM) / 2

const matchRowUnit = (placed: BracketLayoutMatch) =>
  GSL_LOWER_COLUMN_CODES.has(placed.columnCode)
    ? BRACKET_LOWER_ROW_UNIT_EM
    : BRACKET_UNIT_EM

const matchYOffsetFor = (placed: BracketLayoutMatch) =>
  (matchRowUnit(placed) - BRACKET_MATCH_HEIGHT_EM) / 2

const matchTopEm = (placed: BracketLayoutMatch) =>
  placed.y +
  matchYOffsetFor(placed) +
  (isGslLayout.value ? 0 : HEADER_HEIGHT_EM)

const getWinnerCardStyle = (
  winner: BracketLayoutWinner | null
): CSSProperties | undefined => {
  if (!winner) return undefined
  // Тот же yOffset, что у матчей: коннектор считается от центра матча с учётом
  // смещения карточки внутри UNIT_EM.
  return {
    left: em(winner.x),
    top: em(
      winner.y + upperMatchYOffset + (isGslLayout.value ? 0 : HEADER_HEIGHT_EM)
    ),
    width: em(BRACKET_MATCH_WIDTH_EM)
  }
}

const matchStyle = (placed: BracketLayoutMatch): CSSProperties => ({
  left: em(placed.x),
  top: em(matchTopEm(placed)),
  width: em(BRACKET_MATCH_WIDTH_EM)
})

const getConnectorYOffset = (matchId: number) => {
  const placed = layout.value.matches.get(matchId)
  if (!placed) return upperMatchYOffset
  return matchYOffsetFor(placed)
}

const connectorPoints = (connector: BracketLayoutConnector): string => {
  const layoutOffset = isGslLayout.value ? 0 : HEADER_HEIGHT_EM
  const fromY =
    connector.from.y +
    getConnectorYOffset(connector.from.matchId) +
    layoutOffset
  const toY =
    connector.to.y + getConnectorYOffset(connector.to.matchId) + layoutOffset

  if (connector.kind === 'drop') {
    const dropY = fromY + (toY - fromY) * 0.45

    return `${connector.from.x},${fromY} ${connector.from.x},${dropY} ${connector.to.x},${dropY} ${connector.to.x},${toY}`
  }

  const midX = (connector.from.x + connector.to.x) / 2

  return `${connector.from.x},${fromY} ${midX},${fromY} ${midX},${toY} ${connector.to.x},${toY}`
}

const onPick = async (payload: { match: BracketMatchT; slot: BracketSlot }) => {
  const { match, slot } = payload
  const team = getBracketSlotDisplayTeam(slot, false)
  if (!team) return
  if (locked.value || isResultsView.value) return
  if (match.pickable === false) return
  if (
    props.stage.bracket &&
    isBracketMatchLockedByDownstreamPicks(props.stage.bracket, match.id)
  ) {
    return
  }
  if (!tournamentStore.tournament) return

  const isSamePick = match.user_pick_team_id === team.id
  const pickAction: PickemPickAnalyticsMeta['pick_action'] = isSamePick
    ? 'remove'
    : match.user_pick_team_id
      ? 'replace'
      : 'add'
  const picksCountAfter =
    pickAction === 'remove'
      ? props.stage.slots_filled - 1
      : match.user_pick_team_id
        ? props.stage.slots_filled
        : props.stage.slots_filled + 1

  const analyticsMeta: PickemPickAnalyticsMeta = {
    pick_action: pickAction,
    pick_outcome_group: match.column_code,
    pick_slot_index: match.position,
    team,
    stage: props.stage,
    picks_count_after: picksCountAfter
  }

  trackPickClick(analyticsMeta, tournamentStore.tournament)

  try {
    if (isSamePick) {
      await predictionsStore.removeBracket({
        stage: props.stage,
        match,
        analyticsMeta
      })
    } else {
      await predictionsStore.upsertBracket({
        stage: props.stage,
        match,
        team,
        analyticsMeta
      })
    }
  } catch (error) {
    const message = (error as Error)?.message || 'Не удалось сохранить пик'
    if (typeof console !== 'undefined') {
      console.warn('[pickem] bracket mutation failed:', message)
    }
    $snacks?.error?.('Не удалось сохранить пик')
  }
}
</script>

<style lang="scss" module>
.board {
  display: flex;
  flex-direction: column;
  gap: 1.6em;
  padding: 2.4em;
  background-color: color-get(--root-rgb-bg-tone-dark);
  border-radius: 0 0 1.2em 1.2em;

  @include down(md) {
    gap: 2em;
    padding: 0 1.6em;
    border-radius: 0;
    background: rgba(var(--root-rgb-bg-special-1), 1);
  }
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.6em;
  flex-wrap: wrap;
}

.headerInfo {
  display: flex;
  flex-direction: column;
  gap: 0.4em;
}

.stageInfo {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.2em;
}

.timerChip {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  padding: 0.4em 0.6em;
  border-radius: 0.4em;
  background-color: color-get(--root-rgb-accent-2);
  color: color-get(--root-rgb-text-inverse);
  @include typo-get(subhead-s);
  white-space: nowrap;
}

.timerIcon {
  width: 0.9em;
  flex-shrink: 0;
}

.title {
  margin: 0;
  @include typo-get(headline-s);
  color: color-get(--root-rgb-text-1);
}

.subtitle {
  margin: 0;
  @include typo-get(body-s);
  color: color-get(--root-rgb-text-2);
}

.actions {
  display: inline-flex;
  align-items: center;
  gap: 1em;
  flex-wrap: wrap;

  @include down(md) {
    width: 100%;
  }
}

.toggle {
  flex-shrink: 0;
}

.scroll {
  // На любом размере экрана — горизонтальная прокрутка, layout сетки
  // не перестраивается (так задумано в figma: bracket — это «единая»
  // сетка, и её удобнее листать, чем разбивать в столбец).
  overflow-x: auto;
  overflow-y: hidden;
  margin: 0 calc(var(--layout-app-gap, 1.2em) * -1);
  padding: 0 var(--layout-app-gap, 1.2em);
  @include custom-scrollbar();
  &::-webkit-scrollbar {
    height: 0.6em;
  }

  @include down(md) {
    @include hide-scrollbar;
  }
}

.scrollDimmed {
  opacity: 0.5;
}

.viewport {
  position: relative;
  // width/height — размеры из useBracketLayout, передаются через :style.
}

.headers {
  position: absolute;
  inset: 0 0 auto 0;
  height: 2.4em;
  pointer-events: none;
}

.section {
  position: absolute;
  inset: 0 auto auto 0;
  width: 100%;
  pointer-events: none;
}

.sectionTitle {
  margin: 0;
  @include typo-get(body-s);
  color: color-get(--root-rgb-text-3);
}

.headerCell {
  position: absolute;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.4em;
}

// Заголовок колонки: Subhead/L 17/24, цвет text-3 (#555F71). В макете
// «Quarter-finals», «Semifinals», «Grand Final» — все одного цвета.
.columnTitle {
  @include typo-get(subhead-l);
  color: color-get(--root-rgb-text-3);
  text-align: center;
}

.columnTitleWinner {
  color: #ffda35;
}

.winnerCard {
  position: absolute;
  z-index: 1;
}

.connectors {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.matchCard {
  position: absolute;
  z-index: 1;
}
</style>
